import * as React from "react"
import { Head, Link, router, usePage } from "@inertiajs/react"
import { AdminShell } from "@/components/AdminShell"
import { PageHeader } from "@/components/PageHeader"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { PhotoFileInput } from "@/components/ui/photo-file-input"
import { Select } from "@/components/ui/select"
import { TagInput } from "@/components/ui/tag-input"
import { InlineField } from "@/components/ui/inline-edit"
import { useInlineRecord } from "@/hooks/use-inline-record"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import type { PageProps } from "@/types/inertia"

type Photo = {
  id: number
  url: string | null
  full_url: string | null
  alt_text_en: string | null
  alt_text_es: string | null
  label: string | null
  tags: string[]
  media_section_id: number | null
  section_name: string | null
}

// value is a MediaSection id; kind tells apart the venue and built-in rows,
// which are managed from the sections screen rather than here
type SectionOption = { value: string; name: string; kind: string }

type Pagination = { page: number; per_page: number; total: number; pages: number }

type Props = PageProps & {
  photos: Photo[]
  sections: SectionOption[]
  filters: { section?: string; tag?: string }
  pagination: Pagination
}

function EditPhotoDialog({
  photo,
  sections,
  onClose,
}: {
  photo: Photo
  sections: SectionOption[]
  onClose: () => void
}) {
  const fields = useInlineRecord({
    record: {
      alt_text_en: photo.alt_text_en ?? "",
      alt_text_es: photo.alt_text_es ?? "",
      tags: photo.tags ?? [],
      // "" unfiles the photo — Rails casts it to nil for the FK.
      media_section_id: photo.media_section_id ? String(photo.media_section_id) : "",
    },
    url: `/admin/photos/${photo.id}`,
    param: "photo",
  })
  const v = fields.values

  const altEnState = fields.state("alt_text_en")
  const altEsState = fields.state("alt_text_es")
  const sectionState = fields.state("media_section_id")
  const tagsState = fields.state("tags")

  function remove() {
    router.delete(`/admin/photos/${photo.id}`, { onSuccess: onClose })
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit photo</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {photo.url && (
            <img src={photo.url} alt="" className="aspect-video w-full rounded-md object-cover" />
          )}

          <InlineField label="Alt text (English)" htmlFor="alt-text-en" state={altEnState}>
            <Input
              id="alt-text-en"
              value={v.alt_text_en}
              onChange={(e) => fields.set("alt_text_en", e.target.value)}
              onKeyDown={altEnState.keys}
            />
          </InlineField>
          <InlineField label="Alt text (Spanish)" htmlFor="alt-text-es" state={altEsState}>
            <Input
              id="alt-text-es"
              value={v.alt_text_es}
              onChange={(e) => fields.set("alt_text_es", e.target.value)}
              onKeyDown={altEsState.keys}
            />
          </InlineField>
          <InlineField label="Section" htmlFor="section" state={sectionState}>
            <Select
              id="section"
              value={v.media_section_id}
              onChange={(e) => fields.set("media_section_id", e.target.value)}
            >
              <option value="">No section</option>
              {sections.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.name}
                </option>
              ))}
            </Select>
          </InlineField>
          <InlineField label="Tags" htmlFor="tags" state={tagsState}>
            <TagInput
              value={v.tags}
              onChange={(next) => fields.set("tags", next)}
              placeholder="Add a tag…"
            />
          </InlineField>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="secondary"
            className="text-danger-display hover:border-danger hover:bg-danger-faded"
            onClick={remove}
          >
            Remove photo
          </Button>
          <Button type="button" onClick={onClose}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default function PhotosIndex() {
  const { props } = usePage<Props>()
  const { photos, sections, filters, pagination } = props
  const flash = props.flash

  const [section, setSection] = React.useState(filters.section ?? "")
  const [tag, setTag] = React.useState(filters.tag ?? "")
  // The row from the current props, not a snapshot taken when the dialog
  // opened: every inline save refreshes the list, and the dialog should show
  // what the server now holds.
  const [editingId, setEditingId] = React.useState<number | null>(null)
  const editing = photos.find((p) => p.id === editingId) ?? null

  const MAX_BATCH = 10
  const [uploadFiles, setUploadFiles] = React.useState<File[]>([])
  const [batchNote, setBatchNote] = React.useState<string | null>(null)
  const [uploadAltEn, setUploadAltEn] = React.useState("")
  const [uploadAltEs, setUploadAltEs] = React.useState("")
  const [uploadTags, setUploadTags] = React.useState<string[]>([])
  const [uploadSection, setUploadSection] = React.useState("")
  const [uploading, setUploading] = React.useState(false)

  // Always back to page one: a narrower filter usually has fewer pages than
  // the one you were on.
  function applyFilters(page?: number) {
    router.get(
      "/admin/photos",
      { section: section || undefined, tag: tag || undefined, page: page && page > 1 ? page : undefined },
      { preserveState: true, replace: true },
    )
  }

  function upload() {
    if (uploadFiles.length === 0) return
    const fd = new FormData()
    uploadFiles.forEach((f) => fd.append("photo[images][]", f))
    fd.append("photo[alt_text_en]", uploadAltEn)
    fd.append("photo[alt_text_es]", uploadAltEs)
    if (uploadSection) fd.append("photo[media_section_id]", uploadSection)
    uploadTags.forEach((t) => fd.append("photo[tags][]", t))

    setUploading(true)
    router.post("/admin/photos", fd, {
      onFinish: () => setUploading(false),
      onSuccess: () => {
        setUploadFiles([])
        setBatchNote(null)
        setUploadAltEn("")
        setUploadAltEs("")
        setUploadTags([])
        setUploadSection("")
      },
    })
  }

  return (
    <AdminShell>
      <Head title="Photos">
        <meta name="description" content="Central photo library for Sanctuary Therapy Centre." />
        <meta property="og:title" content="Photos" />
        <meta property="og:description" content="Central photo library for Sanctuary Therapy Centre." />
      </Head>

      <PageHeader
        title="Photos"
        description="Upload once, reuse everywhere. Rank and place photos into pages via Pages."
        actions={
          <Button variant="secondary" asChild>
            <Link href="/admin/photos/sections">Manage sections</Link>
          </Button>
        }
      />

      {flash?.notice && <p className="mt-4 text-sm text-accent">{flash.notice}</p>}
      {flash?.alert && <p className="mt-4 text-sm text-danger-display">{flash.alert}</p>}

      {/* Upload */}
      <div className="mt-6 rounded-lg border border-dashed border-hairline p-4">
        <p className="mb-3 text-sm font-medium text-ink-display">Upload photos</p>
        <p className="mb-3 text-sm text-ink-muted">
          Pick up to {MAX_BATCH} at a time — section, alt text, and tags are optional and apply to
          the whole batch. You can assign or change them per photo afterwards by tapping it in the
          grid.
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="flex flex-wrap items-center gap-2">
            <PhotoFileInput
              multiple
              size="md"
              onSelect={(files) => {
                setUploadFiles(files.slice(0, MAX_BATCH))
                setBatchNote(
                  files.length > MAX_BATCH
                    ? `Only the first ${MAX_BATCH} photos were kept — upload the rest as another batch.`
                    : null,
                )
              }}
            />
            {uploadFiles.length > 0 && (
              <p className="text-sm text-ink-muted">
                {uploadFiles.length === 1
                  ? uploadFiles[0].name
                  : `${uploadFiles.length} photos selected`}
              </p>
            )}
          </div>
          <Select value={uploadSection} onChange={(e) => setUploadSection(e.target.value)}>
            <option value="">No section</option>
            {sections.map((s) => (
              <option key={s.value} value={s.value}>
                {s.name}
              </option>
            ))}
          </Select>
          <Input
            placeholder="Alt text (English)"
            value={uploadAltEn}
            onChange={(e) => setUploadAltEn(e.target.value)}
          />
          <Input
            placeholder="Alt text (Spanish)"
            value={uploadAltEs}
            onChange={(e) => setUploadAltEs(e.target.value)}
          />
          <TagInput
            value={uploadTags}
            onChange={setUploadTags}
            placeholder="Tags…"
            className="sm:col-span-2"
          />
        </div>
        {batchNote && <p className="mt-2 text-sm text-danger-display">{batchNote}</p>}
        <Button
          type="button"
          className="mt-3"
          disabled={uploadFiles.length === 0 || uploading}
          onClick={upload}
        >
          {uploadFiles.length > 1 ? `Upload ${uploadFiles.length} photos` : "Upload photo"}
        </Button>
      </div>

      {/* Filters */}
      <div className="mt-6 flex flex-wrap gap-3">
        <Select value={section} onChange={(e) => setSection(e.target.value)} className="w-48">
          <option value="">All sections</option>
          <option value="none">Unfiled</option>
          {sections.map((s) => (
            <option key={s.value} value={s.value}>
              {s.name}
            </option>
          ))}
        </Select>
        <Input
          placeholder="Filter by tag…"
          value={tag}
          onChange={(e) => setTag(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && applyFilters()}
          className="w-48"
        />
        <Button variant="secondary" onClick={() => applyFilters()}>
          Filter
        </Button>
      </div>

      {/* Grid */}
      <div className="mt-6 grid gap-4 sm:grid-cols-3 lg:grid-cols-4">
        {photos.length === 0 && (
          <p className="col-span-full py-8 text-center text-ink-muted">No photos found.</p>
        )}
        {photos.map((photo) => (
          <button
            key={photo.id}
            type="button"
            onClick={() => setEditingId(photo.id)}
            className="cursor-pointer overflow-hidden rounded-lg border border-hairline text-left transition-colors hover:border-accent"
          >
            {photo.url ? (
              <img src={photo.url} alt={photo.alt_text_en ?? ""} className="aspect-video w-full object-cover" />
            ) : (
              <div className="flex aspect-video items-center justify-center bg-surface text-xs text-ink-muted">
                No preview
              </div>
            )}
            <div className="space-y-1 p-2.5">
              {photo.section_name && <p className="text-xs font-medium text-ink-display">{photo.section_name}</p>}
              {photo.tags.length > 0 && (
                <p className="truncate text-[11px] text-ink-muted">{photo.tags.join(", ")}</p>
              )}
            </div>
          </button>
        ))}
      </div>

      {pagination.total > 0 && (
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <p className="text-sm text-ink-muted">
            Showing {(pagination.page - 1) * pagination.per_page + 1}–
            {Math.min(pagination.page * pagination.per_page, pagination.total)} of {pagination.total}
          </p>
          {pagination.pages > 1 && (
            <div className="ml-auto flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={pagination.page <= 1}
                onClick={() => applyFilters(pagination.page - 1)}
              >
                Previous
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={pagination.page >= pagination.pages}
                onClick={() => applyFilters(pagination.page + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </div>
      )}

      {editing && (
        <EditPhotoDialog photo={editing} sections={sections} onClose={() => setEditingId(null)} />
      )}
    </AdminShell>
  )
}
